import React, { useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';
import { Appointment } from '../services/appointmentService';
import { AppointmentType } from '../services/appointmentTypeService';
import { startOfDay, endOfDay, parseISO, subMonths, addMonths } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { indexedDbService } from '../services/indexedDbService';
import { appointmentService } from '../services/appointmentService';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Stack,
  Skeleton
} from '@mui/material';
import { useAppointments } from '../hooks/useAppointments';
import { useAppointmentTypes } from '../hooks/useAppointmentTypes';

const Dashboard: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { theme, businessId } = useBusinessTheme();
  const navigate = useNavigate();

  // Get today's date range
  const today = new Date();
  const startOfToday = startOfDay(today).toISOString();
  const endOfToday = endOfDay(today).toISOString();

  // Use TanStack Query hooks
  const {
    data: appointmentTypes = [],
    isLoading: typesLoading
  } = useAppointmentTypes({
    businessId: businessId || undefined,
    enabled: !!businessId
  });

  const {
    data: todayAppointments = [],
    isLoading: appointmentsLoading,
    error: appointmentsError
  } = useAppointments({
    startDate: startOfToday,
    endDate: endOfToday,
    enabled: isAuthenticated && !!businessId
  });

  const loading = typesLoading || appointmentsLoading;
  const error = appointmentsError ? 'Failed to load data' : null;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Prefetch appointments for calendar in background
  useEffect(() => {
    if (!user || !businessId) return;

    const prefetchAppointments = async () => {
      const start = subMonths(new Date(), 1);
      const end = addMonths(new Date(), 1);

      try {
        console.log('Dashboard: Prefetching appointments for calendar cache');
        const appointments = await appointmentService.getAppointments({
          startDate: start.toISOString(),
          endDate: end.toISOString()
        });

        // Merge with existing cache to preserve any appointments outside this range
        await indexedDbService.mergeCachedAppointments(appointments, businessId, { start, end });
        await indexedDbService.markRangeFetched(start, end, businessId);

        console.log(`Dashboard: Prefetched ${appointments.length} appointments for calendar`);
      } catch (error) {
        console.error('Dashboard: Failed to prefetch appointments:', error);
        // Don't show error to user - prefetch is background operation
      }
    };

    // Run in background after a short delay to not block dashboard loading
    const prefetchTimer = setTimeout(prefetchAppointments, 1000);

    return () => clearTimeout(prefetchTimer);
  }, [user, businessId]);

  // Helper component for appointment loading skeleton
  const AppointmentSkeleton = () => (
    <List>
      {Array.from({ length: 3 }).map((_, index) => (
        <React.Fragment key={`skeleton-${index}`}>
          <ListItem sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start', gap: 2 }}>
              <Skeleton variant="circular" width={12} height={12} sx={{ mt: 0.5 }} />
              <Box sx={{ flex: 1 }}>
                <Stack spacing={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Skeleton variant="circular" width={16} height={16} />
                    <Skeleton variant="text" width={150} height={20} />
                  </Box>
                  <Skeleton variant="text" width="100%" height={24} />
                  <Skeleton variant="text" width="80%" height={20} />
                  {index < 2 && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Skeleton variant="circular" width={16} height={16} />
                      <Skeleton variant="text" width={200} height={20} />
                    </Box>
                  )}
                </Stack>
              </Box>
            </Box>
          </ListItem>
          {index < 2 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  );

  // Helper function to get appointment type color
  const getAppointmentColor = (appointment: Appointment): string => {
    const appointmentType = appointmentTypes.find(type => type.id === appointment.appointmentTypeId);
    return appointmentType?.color || '#1976d2';
  };

  // Helper function to get appointment type text color
  const getAppointmentTextColor = (appointment: Appointment): string => {
    const appointmentType = appointmentTypes.find(type => type.id === appointment.appointmentTypeId);
    return appointmentType?.textColor || '#ffffff';
  };

  const getUpcomingAppointments = () => {
    const now = new Date();

    // Return today's appointments that haven't ended yet
    // Sort by: all-day appointments first, then by start time
    return todayAppointments
      .filter(appointment => {
        const appointmentEndTime = parseISO(appointment.endTime);
        return appointmentEndTime > now;
      })
      .sort((a, b) => {
        // All-day appointments come first
        if (a.isAllDay && !b.isAllDay) return -1;
        if (!a.isAllDay && b.isAllDay) return 1;

        // Within the same type (both all-day or both timed), sort by start time
        return parseISO(a.startTime).getTime() - parseISO(b.startTime).getTime();
      });
  };


  return (
    <>
      <Helmet>
        <title>Dashboard | MindShare Calendar</title>
      </Helmet>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome, {user?.firstName}!
        </Typography>

        <Grid container spacing={3}>
          {/* Today's Appointments */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, backgroundColor: theme.accentColor }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Today's Appointments
              </Typography>
              {loading ? (
                <AppointmentSkeleton />
              ) : error ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                  <Typography color="error">{error}</Typography>
                </Box>
              ) : (
                <List>
                  {getUpcomingAppointments().map((appointment, index) => {
                  const appointmentColor = getAppointmentColor(appointment);
                  const hasCustomer = appointment.customerId && appointment.customerName;

                  return (
                    <React.Fragment key={appointment.id}>
                      <ListItem
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'stretch'
                        }}
                      >
                        <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start', gap: 2 }}>
                          {/* Colored circle for appointment type */}
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: appointmentColor,
                              flexShrink: 0,
                              mt: 0.5
                            }}
                          />

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack spacing={0.5}>
                              {/* Time - all-day or time range */}
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <AccessTimeIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                  {appointment.isAllDay ? (
                                    'All-day'
                                  ) : (
                                    `${formatInTimeZone(
                                      parseISO(appointment.startTime),
                                      Intl.DateTimeFormat().resolvedOptions().timeZone,
                                      'p'
                                    )} - ${formatInTimeZone(
                                      parseISO(appointment.endTime),
                                      Intl.DateTimeFormat().resolvedOptions().timeZone,
                                      'p'
                                    )}`
                                  )}
                                </Typography>
                              </Stack>

                              {/* Customer name if available */}
                              {hasCustomer && (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <PersonIcon fontSize="small" color="action" />
                                  <Typography variant="caption">
                                    {appointment.customerName}
                                  </Typography>
                                </Stack>
                              )}

                              {/* Customer phone if available */}
                              {hasCustomer && appointment.customerPhone && (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <PhoneIcon fontSize="small" color="action" />
                                  <Typography variant="caption" color="text.secondary">
                                    {appointment.customerPhone}
                                  </Typography>
                                </Stack>
                              )}

                              {/* Address if available */}
                              {appointment.address && (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <LocationOnIcon fontSize="small" color="action" />
                                  <Typography variant="caption" color="text.secondary">
                                    {appointment.address}
                                  </Typography>
                                </Stack>
                              )}

                              {/* Description if no customer or as additional info */}
                              {appointment.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ pl: 3.5 }}>
                                  {appointment.description}
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        </Box>
                      </ListItem>
                      {/* Only add a divider if it's not the last item */}
                      {index < getUpcomingAppointments().length - 1 && (
                        <Divider />
                      )}
                    </React.Fragment>
                  );
                })}
                {getUpcomingAppointments().length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography color="text.secondary">
                          No appointments scheduled for today
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  endIcon={<ChevronRightIcon />}
                  component={RouterLink}
                  to="/calendar"
                >
                  View
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Dashboard;
